import { useCallback, useEffect, useMemo, useState } from "react";
import { useTrendingNichesStore } from "../store/trendingNichesStore";
import { apiNiches } from "../services";
import { INiche , IErrorData } from "../services/types";
import { useExperiment } from "@/common/hooks/useExperiment";
import { EXPERIMENT_TRENDING_NICHES, VARIANT_SEARCH } from "@/common/services/experiment/constants";
import { convertException } from "@/common/utils/convertException";

export const useTrendingNiches = () => {
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [error, setError] = useState<IErrorData | null>(null);
  const [data, setData] = useState<{ niches: INiche[] } | null>(null);

  const setTrendingNiches = useTrendingNichesStore((state) => state.setTrendingNiches);
  const trendingNiches = useTrendingNichesStore((state) => state.trendingNiches);
  const { variant, isLoading: isExperimentLoading } = useExperiment({ key: EXPERIMENT_TRENDING_NICHES, fallback: VARIANT_SEARCH });

  const isLoading = isExperimentLoading || isDataLoading;

  const fetchTrendingNiches = useCallback(async () => {
    setIsDataLoading(true);
    setError(null);

    try {
      const result = await apiNiches.getTrendingNiches(variant as string);
      setData(result);
    } catch (err) {
      setError(convertException(err));
    } finally {
      setIsDataLoading(false);
    }
  }, [variant]);

  useEffect(() => {
    if (isExperimentLoading) {
      return;
    }

    (async () => {
      await fetchTrendingNiches();
    })();
  }, [isExperimentLoading]);

  useEffect(() => {
    if (!error && data && data.niches) {
      setTrendingNiches(data.niches);
    }
  }, [error, data, setTrendingNiches]);

  const niches = useMemo(() =>
    trendingNiches.map((el: INiche) => ({
      id: el.id,
      name: el.name,
      image: el.thumbnail,
      subscribers: el.subscribers,
    })), [trendingNiches]);

  return {
    trendingNiches: niches,
    error,
    isLoading
  };
};

